import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // GitHub Flavored Markdown (includes tables, strikethrough, etc.)

const GitlabMarkdownRenderer = ({ content, className = "" }) => {
  return (
    <div className={`gitlab-markdown ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Paragraph
          p: ({ node, ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
          
          // Headings
          h1: ({ node, children, ...props }) => <h1 className="text-3xl font-bold mt-6 mb-4 text-gray-900 border-b pb-2" {...props}>{children}</h1>,
          h2: ({ node, children, ...props }) => <h2 className="text-2xl font-bold mt-6 mb-4 text-gray-900 border-b pb-2" {...props}>{children}</h2>,
          h3: ({ node, children, ...props }) => <h3 className="text-xl font-bold mt-5 mb-3 text-gray-900" {...props}>{children}</h3>,
          h4: ({ node, children, ...props }) => <h4 className="text-lg font-bold mt-4 mb-2 text-gray-900" {...props}>{children}</h4>,
          h5: ({ node, children, ...props }) => <h5 className="text-base font-bold mt-3 mb-2 text-gray-900" {...props}>{children}</h5>,
          h6: ({ node, children, ...props }) => <h6 className="text-sm font-bold mt-2 mb-1 text-gray-900" {...props}>{children}</h6>,
          
          // Emphasis
          strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
          em: ({ node, ...props }) => <em className="italic text-gray-700" {...props} />,
          
          // Lists
          ul: ({ node, ...props }) => <ul className="list-disc list-inside ml-4 mb-4 space-y-1" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal list-inside ml-4 mb-4 space-y-1" {...props} />,
          li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
          
          code: ({ node, className, children, ...props }) => {
            const isInlineCode = !node?.parentNode?.properties?.nodeName || 
                                  node.parentNode.properties.nodeName !== 'pre';
            
            if (isInlineCode) {
              return <span {...props}>{children}</span>;
            }
            
            // Extract language from className (e.g., "language-js")
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            return (
              <div className="my-4">
                <div className="bg-gray-800 text-gray-200 px-4 py-1 text-xs font-sans rounded-t flex justify-between items-center">
                  <span>{language ? language.toUpperCase() : 'CODE'}</span>
                  <button 
                    className="text-gray-300 hover:text-white text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(children?.toString() || '');
                    }}
                  >
                    Copy
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-200 p-4 rounded-b overflow-x-auto text-sm font-mono whitespace-pre-wrap">
                  <code className="" {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          
          // Preformatted text
          pre: ({ node, ...props }) => <pre className="bg-gray-900 text-gray-200 p-4 rounded my-4 overflow-x-auto text-sm" {...props} />,
          
          // Links
          a: ({ node, children, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>,
          
          // Blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 text-gray-700 italic" {...props} />
          ),
          
          // Horizontal rule
          hr: ({ node, ...props }) => <hr className="my-6 border-gray-300" {...props} />,
          
          // Tables (with GitLab-style support via remarkGfm)
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-gray-300">
                {props.children}
              </table>
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
          tbody: ({ node, ...props }) => <tbody {...props} />,
          tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-300 px-4 py-2 font-semibold text-left bg-gray-100 text-gray-700" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-300 px-4 py-2 text-left text-gray-700" {...props} />
          ),
          
          // Strikethrough (from GFM)
          del: ({ node, ...props }) => <del className="text-gray-500" {...props} />,
        }}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
};

export default GitlabMarkdownRenderer;